/**
 * 上传头像前的本地预处理：裁正方形 + 缩到小图 + 转 JPEG。
 *
 * 全部在浏览器里做完，理由和 `avatarStorage.ts` 服务端那边选择「不做图片处理」
 * 完全对称：服务器只有 1G 内存，压缩这种活儿理应在客户端做，服务器只负责
 * 「这是不是图片、大不大」的把关，两边各司其职。
 *
 * 几何计算（裁多大、贴哪）是纯函数，可以在 node 里完整测试；真正碰
 * `Image` / `canvas` / `FileReader` 的部分只是把纯函数的结果应用到 DOM API 上，
 * 没有分支逻辑可测，靠人工核对就够了。
 */

/** 输出的正方形边长。够贴清晰，又不至于让传上去的字节数太大。 */
export const AVATAR_OUTPUT_SIZE = 256
export const AVATAR_JPEG_QUALITY = 0.85
/** 原图超过这个大小就直接拒绝，不进解码：避免选了张几十 MB 的照片把标签页卡死 */
export const MAX_SOURCE_FILE_BYTES = 20 * 1024 * 1024

export interface Rect {
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
}

/**
 * 从 `srcWidth × srcHeight` 的图里，取「能放进去的最大正方形」的裁切框，居中对齐。
 * 横图裁左右、竖图裁上下——这是所有头像裁剪工具的通行做法，比拉伸变形好看得多。
 */
export function centerSquareCrop(srcWidth: number, srcHeight: number): Rect {
  const side = Math.min(srcWidth, srcHeight)
  return {
    x: (srcWidth - side) / 2,
    y: (srcHeight - side) / 2,
    width: side,
    height: side,
  }
}

export function isImageFile(file: { type: string; size: number }): boolean {
  return file.type.startsWith('image/') && file.size > 0 && file.size <= MAX_SOURCE_FILE_BYTES
}

/** `FileReader` 的 Promise 封装。 */
export function readFileAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      resolve(typeof reader.result === 'string' ? reader.result : '')
    }
    reader.onerror = () => reject(reader.error ?? new Error('读取文件失败'))
    reader.readAsDataURL(file)
  })
}

/**
 * 把一张 data URL 裁正方形、缩到 `AVATAR_OUTPUT_SIZE`、转成 JPEG data URL。
 * 失败（不是图片、解码出错）一律 reject，调用方翻译成人话。
 */
export function downscaleToAvatarDataUrl(dataUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      const crop = centerSquareCrop(img.naturalWidth, img.naturalHeight)
      const canvas = document.createElement('canvas')
      canvas.width = AVATAR_OUTPUT_SIZE
      canvas.height = AVATAR_OUTPUT_SIZE
      const ctx = canvas.getContext('2d')
      if (ctx === null) {
        reject(new Error('这台设备不支持图片处理'))
        return
      }
      ctx.drawImage(
        img,
        crop.x,
        crop.y,
        crop.width,
        crop.height,
        0,
        0,
        AVATAR_OUTPUT_SIZE,
        AVATAR_OUTPUT_SIZE,
      )
      resolve(canvas.toDataURL('image/jpeg', AVATAR_JPEG_QUALITY))
    }
    img.onerror = () => reject(new Error('图片解码失败，换一张试试'))
    img.src = dataUrl
  })
}

/** 一步到位：选中的文件 → 能直接传给 `uploadCustomAvatar` 的 data URL。 */
export async function prepareAvatarUpload(file: File): Promise<string> {
  if (!isImageFile(file)) throw new Error('请选择一张图片文件（不超过 20MB）')
  const raw = await readFileAsDataUrl(file)
  return downscaleToAvatarDataUrl(raw)
}
